import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { SrIcon } from "@/components/ui/SrIcon";
import { useBillingRateBreakdown } from "@/hooks/use-billing-rate-breakdown";
import { formatDate, formatPct, MONTH_NAMES_NB } from "@/lib/format";
import { TONE_PILL, TONE_TEXT } from "@/lib/widget-status";

interface BillingRateBreakdownDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  fullName: string;
  year: number;
  month: number | null; // 1-12
  capacity: number;      // kapasitet − fravær for måneden
  rate: number | null;   // ferdig beregnet grad for måneden
}

export function BillingRateBreakdownDialog({
  open, onOpenChange, userId, fullName, year, month, capacity, rate,
}: BillingRateBreakdownDialogProps) {
  const { data, isLoading } = useBillingRateBreakdown(userId, year, open ? month : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {fullName} — {month != null ? MONTH_NAMES_NB[month - 1] : ""} {year}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground text-xs uppercase tracking-wider">
                    <th className="text-left py-2 font-medium">Prosjekt</th>
                    <th className="text-left py-2 font-medium">Oppgave</th>
                    <th className="text-right py-2 font-medium">Tildelte timer</th>
                    <th className="text-left py-2 font-medium pl-3">Status</th>
                    <th className="text-right py-2 font-medium">Periodisert</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.rows ?? []).map((r) => (
                    <tr
                      key={r.budgetLineId}
                      className={`border-b border-border/40 last:border-0 ${r.isZeroHours ? TONE_PILL.miss : ""}`}
                    >
                      <td className="py-2 font-medium">{r.projectCode ?? "—"}</td>
                      <td className="py-2 text-muted-foreground">{r.task}</td>
                      <td className="py-2 text-right tabular-nums">
                        {r.assignedHours}t
                        {r.isZeroHours && (
                          <span className={`ml-1 text-xs ${TONE_TEXT.miss}`}>(ingen timer tildelt)</span>
                        )}
                      </td>
                      <td className="py-2 pl-3 text-xs">{r.taskStatus}</td>
                      <td className="py-2 text-right text-muted-foreground tabular-nums">
                        {formatDate(r.attributedAt)}
                      </td>
                    </tr>
                  ))}
                  {(data?.rows.length ?? 0) === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">
                      Ingen fakturerbare oppgaver denne måneden.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="rounded-md bg-muted/40 p-4 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sum fakturerbare timer</span>
                <span className="tabular-nums font-medium">{data?.totalAssignedHours ?? 0}t</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Kapasitet (− fravær)</span>
                <span className="tabular-nums font-medium">{Math.round(capacity * 10) / 10}t</span>
              </div>
              <div className="flex justify-between border-t border-border/50 pt-1 mt-1">
                <span className="font-medium">Faktureringsgrad</span>
                <span className="tabular-nums font-bold">
                  {rate != null ? formatPct(rate) : "—"}
                </span>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
